/**
 * The cockpit's menu on the shared Sidebar of @digitalisation/enlixy-comp:
 * from lg up an icon rail docked under the header (it follows the header's
 * height, --app-header-height), and the full menu (pages, NATS status) as a
 * drawer at every width, under the header like the rail, opened by the
 * header's menu button (below lg) or the rail's top button. From lg the drawer
 * unfolds out of the rail: the toggle and the entries keep their place, the
 * labels appear next to them. The header carries the name, so the menu has no
 * title; account, demo data and sign-out are in the header's avatar menu
 * (CockpitUserMenu).
 */

import type { MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  cx,
  Icon,
  Sidebar,
  sidebarIconClass,
  sidebarItemClass,
  type IconLike,
  type SidebarItem,
} from "@digitalisation/enlixy-comp";
import { useNATSStatus } from "../hooks/useNATSStatus";
import { useSAPDataStore } from "../store/SAPDataStore";

const EDITOR_URL = "https://editor.enlixy.com";
const UPLOADER_URL = import.meta.env.VITE_UPLOADER_URL || "https://uploader.enlixy.com";

/** Outline icons the package has no glyph for (24 px grid, like its own). */
const outline = (...d: string[]) => (
  <svg
    viewBox="0 0 24 24"
    width={18}
    height={18}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0"
    aria-hidden="true"
  >
    {d.map((p) => (
      <path key={p} d={p} />
    ))}
  </svg>
);
const ICONS = {
  compare: outline("M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"),
  editor: outline(
    "M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z",
  ),
  tasks: outline(
    "M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  ),
};

type Entry = { id: string; label: string; icon: IconLike } & (
  | { to: string }
  | { href: string }
  | { soon: true }
);

/** The page a path belongs to (machine and line pages have none). */
function activeEntry(pathname: string): string | undefined {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/cockpit/sessions")) return "sessions";
  if (pathname.startsWith("/compare-machine")) return "compare";
  return undefined;
}

export function CockpitSidebar({
  open,
  onOpen,
  onClose,
}: {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const nats = useNATSStatus();
  const s = (key: string) => t(`sidebar.${key}`);

  // home: back to the start page as it was left (tab, view, open tree nodes)
  const goHome = (e: MouseEvent) => {
    e.preventDefault();
    const { getHomeViewState, showOnlyWithData } = useSAPDataStore.getState();
    const saved = getHomeViewState();
    const params = new URLSearchParams({
      tab: saved.activeTab,
      viewMode: saved.viewMode,
      showOnlyWithData: showOnlyWithData ? "true" : "false",
    });
    const expanded = Array.from(saved.expandedNodes);
    if (expanded.length > 0) params.set("expandedNodes", encodeURIComponent(expanded.join("|")));
    navigate(`/?${params.toString()}`);
    onClose();
  };

  const entries: Entry[] = [
    { id: "home", label: t("home"), icon: "home", to: "/" },
    { id: "sessions", label: t("cockpitSessions"), icon: "message", to: "/cockpit/sessions" },
    { id: "compare", label: t("compareMachine"), icon: ICONS.compare, to: "/compare-machine" },
    { id: "editor", label: s("dashboardEditor"), icon: ICONS.editor, href: EDITOR_URL },
    { id: "tasks", label: s("taskBasedMaintenance"), icon: ICONS.tasks, soon: true },
    { id: "acceptance", label: s("machineAcceptance"), icon: "award", soon: true },
    { id: "anomaly", label: s("anomalyDetection"), icon: "alert", soon: true },
    { id: "uploader", label: s("machineUploader"), icon: "upload", href: UPLOADER_URL },
  ];

  const text = (label: string, collapsed: boolean) => (
    <span className={collapsed ? "sr-only" : "min-w-0 flex-1 truncate"}>{label}</span>
  );
  const items: SidebarItem[] = entries.map((e): SidebarItem => {
    const base = { id: e.id, label: e.label, icon: e.icon };
    if ("soon" in e) {
      return { ...base, disabled: true, badge: <span className="text-[10px] tracking-wide uppercase opacity-70">{s("soon")}</span> };
    }
    if ("href" in e) {
      return {
        ...base,
        render: (cls, _active, collapsed) => (
          <a href={e.href} target="_blank" rel="noopener noreferrer" className={cls} title={collapsed ? e.label : undefined} onClick={onClose}>
            {renderEntryIcon(e.icon)}
            {text(e.label, collapsed)}
            {!collapsed && <Icon name="external" size={13} className="shrink-0 opacity-60" aria-hidden="true" />}
          </a>
        ),
      };
    }
    return {
      ...base,
      render: (cls, _active, collapsed) => (
        <NavLink
          to={e.to}
          end={e.to === "/"}
          className={cls}
          title={collapsed ? e.label : undefined}
          onClick={e.id === "home" ? goHome : onClose}
        >
          {renderEntryIcon(e.icon)}
          {text(e.label, collapsed)}
        </NavLink>
      ),
    };
  });

  const status = nats.isConnected ? t("connected") : t("disconnected");
  const dot = (
    <span
      aria-hidden="true"
      className={cx("h-2 w-2 shrink-0 rounded-full", nats.isConnected ? "bg-emerald-400" : "bg-red-400")}
    />
  );

  return (
    <Sidebar
      label={s("label")}
      items={items}
      activeId={activeEntry(pathname)}
      open={open}
      onClose={onClose}
      // the rail runs on under the header's rounded bottom (40 px radius from
      // sm up), so the menu colour shows in its corner; its items start below
      railTop="calc(var(--app-header-height, 80px) - 40px)"
      offsetTop={40}
      footer={
        <p className="flex items-center gap-2 px-3 text-xs text-on-sidebar/80" role="status">
          {dot}
          NATS: {status}
          {!nats.isConnected && nats.lastConnected && (
            <span className="text-on-sidebar/60">
              · {t("lastConnected")} {nats.lastConnected.toLocaleTimeString()}
            </span>
          )}
        </p>
      }
      // one toggle in one place: "open" at the top of the rail, "close" at the
      // same height in the open menu
      railHeader={(expanded) => (
        <button
          type="button"
          onClick={expanded ? onClose : onOpen}
          aria-expanded={expanded}
          aria-label={expanded ? undefined : s("expand")}
          title={expanded ? undefined : s("expand")}
          data-tour={expanded ? undefined : "menu-button"}
          className={cx(sidebarItemClass(), !expanded && "justify-center")}
        >
          <span className={sidebarIconClass}>
            <PanelIcon expanded={expanded} />
          </span>
          {expanded && <span className="min-w-0 flex-1 truncate">{s("close")}</span>}
        </button>
      )}
      railFooter={
        <span
          role="img"
          aria-label={`NATS: ${status}`}
          title={`NATS: ${status}`}
          className="flex h-4 items-center justify-center"
        >
          {dot}
        </span>
      }
    />
  );
}

/** The side panel toggle: a window with its left panel, the arrow out of it (open) or back in (close). */
function PanelIcon({ expanded }: { expanded: boolean }) {
  return outline("M4 5h16v14H4z", "M9 5v14", expanded ? "M15 10l-2 2 2 2" : "M13 10l2 2-2 2");
}

/**
 * An entry's icon (a package glyph name or one of ICONS) in the sidebar's icon
 * box, so it keeps its place and the row its height when the rail extends.
 */
function renderEntryIcon(icon: IconLike): ReactNode {
  return (
    <span className={sidebarIconClass}>
      {typeof icon === "string" ? <Icon name={icon as never} size={18} className="shrink-0" aria-hidden="true" /> : icon}
    </span>
  );
}
